import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, FolderKanban, Bot, Rocket, Settings,
  CreditCard, HeartPulse, Store, Users, Cpu, Plus, Zap, Search,
  UserPlus, RotateCcw, Smartphone,
  TrendingUp, Sparkles, Clock, ArrowRight, Command, X,
  Star, Bell, Mic, MicOff, Undo2, Hash, Play, Tag,
} from "lucide-react";
import {
  allCommands, getRecentCommands, addToHistory, isAIBuildQuery,
  getFavoriteCommands, toggleFavorite, isFavorite, getSuggestedCommands,
  getMacros, findMacroByTrigger, pushUndo, popUndo,
  getUndoStack,
  type PaletteCommand, type CommandMacro,
} from "@/services/commandPaletteService";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nContext";

const iconMap: Record<string, React.ElementType> = {
  LayoutDashboard, FolderKanban, Bot, Rocket, Settings,
  CreditCard, HeartPulse, Store, Users, Cpu, Plus, Zap, Search,
  UserPlus, RotateCcw, Smartphone,
  TrendingUp, Sparkles, Bell, Star, Mic, Tag,
};

function getIcon(name: string) {
  return iconMap[name] || Search;
}

type PaletteMode = "default" | "voice" | "macro";

export function CommandPalette() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [resultCount, setResultCount] = useState(0);
  const [mode, setMode] = useState<PaletteMode>("default");
  const [voiceListening, setVoiceListening] = useState(false);
  const [confirmAction, setConfirmAction] = useState<PaletteCommand | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === "Escape") {
        if (confirmAction) setConfirmAction(null);
        else setOpen(false);
      }
      // Ctrl+Z for undo
      if (e.key === "z" && (e.metaKey || e.ctrlKey) && !e.shiftKey && open) {
        e.preventDefault();
        handleUndo();
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  // handleUndo is defined later via useCallback - stable reference, intentionally omitted
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, confirmAction]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      setMode("default");
      setConfirmAction(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setVoiceListening(false);
    }
  }, [open]);

  // open is intentionally listed to re-fetch these when the palette opens/closes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const recentCommands = useMemo(() => getRecentCommands(6), [open]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const favoriteCommands = useMemo(() => getFavoriteCommands(), [open]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const suggestedCommands = useMemo(() => getSuggestedCommands(location.pathname), [open, location.pathname]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const macros = useMemo(() => getMacros(), [open]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const undoStack = useMemo(() => getUndoStack(), [open]);

  // Filter commands
  const filteredCommands = useMemo(() => {
    if (!query.trim()) return [];
    const lower = query.toLowerCase();
    const terms = lower.split(/\s+/);
    return allCommands
      .map(cmd => {
        const haystack = `${cmd.label} ${cmd.keywords} ${cmd.description || ""}`.toLowerCase();
        const score = terms.reduce((s, t) => s + (haystack.includes(t) ? 1 : 0), 0);
        return { cmd, score };
      })
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(r => r.cmd);
  }, [query]);

  // Macro match
  const matchedMacro = useMemo(() => {
    if (query.startsWith("/")) return findMacroByTrigger(query.trim());
    return undefined;
  }, [query]);

  const showAIBuild = useMemo(() => isAIBuildQuery(query), [query]);

  const sections = useMemo(() => {
    const result: { label: string; icon: React.ElementType; items: PaletteCommand[] }[] = [];
    if (query.trim()) {
      if (filteredCommands.length > 0) {
        const navs = filteredCommands.filter(c => c.category === "navigation");
        const acts = filteredCommands.filter(c => c.category === "action");
        if (navs.length) result.push({ label: t("cmdk.section.navigate"), icon: ArrowRight, items: navs });
        if (acts.length) result.push({ label: t("cmdk.section.actions"), icon: Zap, items: acts });
      }
    } else {
      if (favoriteCommands.length > 0) result.push({ label: t("cmdk.section.favorites"), icon: Star, items: favoriteCommands.slice(0, 5) });
      if (suggestedCommands.length > 0) result.push({ label: t("cmdk.section.suggested"), icon: Sparkles, items: suggestedCommands });
      if (recentCommands.length > 0) result.push({ label: t("cmdk.section.recent"), icon: Clock, items: recentCommands });
    }
    return result;
  }, [query, filteredCommands, favoriteCommands, suggestedCommands, recentCommands, t]);

  const flatItems = useMemo(() => {
    const items: { type: "ai" | "macro" | "cmd"; cmd?: PaletteCommand; macro?: CommandMacro }[] = [];
    if (showAIBuild) items.push({ type: "ai" });
    if (matchedMacro) items.push({ type: "macro", macro: matchedMacro });
    sections.forEach(s => s.items.forEach(cmd => items.push({ type: "cmd", cmd })));
    return items;
  }, [sections, showAIBuild, matchedMacro]);

  useEffect(() => { setResultCount(flatItems.length); }, [flatItems]);

  // Execute - runCommand is defined below; it's stable (only depends on navigate)
  const executeCommand = useCallback((cmd: PaletteCommand) => {
    if (cmd.destructive) {
      setConfirmAction(cmd);
      return;
    }
    runCommand(cmd);
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const runCommand = useCallback((cmd: PaletteCommand) => {
    addToHistory(cmd.id);
    pushUndo({ id: crypto.randomUUID(), label: cmd.label, commandId: cmd.id, timestamp: Date.now(), route: cmd.route });
    if (cmd.route) navigate(cmd.route);
    setOpen(false);
    setConfirmAction(null);
    toast({ title: `→ ${cmd.label}`, description: cmd.description || t("cmdk.toast.executing") });
  }, [navigate, t]);

  const executeAIBuild = useCallback(() => {
    navigate(`/dashboard?prompt=${encodeURIComponent(query)}`);
    setOpen(false);
    toast({ title: t("cmdk.toast.build"), description: `"${query}"` });
  }, [navigate, query, t]);

  const executeMacro = useCallback((macro: CommandMacro) => {
    macro.steps.forEach(stepId => {
      const cmd = allCommands.find(c => c.id === stepId);
      if (cmd) addToHistory(cmd.id);
    });
    const lastStep = allCommands.find(c => c.id === macro.steps[macro.steps.length - 1]);
    if (lastStep?.route) navigate(lastStep.route);
    setOpen(false);
    toast({ title: t("cmdk.toast.macro", { name: macro.name }), description: t("cmdk.toast.executedSteps", { n: macro.steps.length }) });
  }, [navigate, t]);

  const handleUndo = useCallback(() => {
    const action = popUndo();
    if (action) {
      toast({ title: t("cmdk.toast.undone"), description: action.label });
    } else {
      toast({ title: t("cmdk.toast.nothingToUndo") });
    }
  }, [t]);

  const handleToggleFavorite = useCallback((e: React.MouseEvent, cmdId: string) => {
    e.stopPropagation();
    const added = toggleFavorite(cmdId);
    toast({ title: added ? t("cmdk.toast.addedFavorite") : t("cmdk.toast.removedFavorite") });
  }, [t]);

  // Voice simulation
  const toggleVoice = useCallback(() => {
    setVoiceListening(prev => {
      if (!prev) {
        toast({ title: t("cmdk.toast.listening"), description: t("cmdk.toast.sayCommand") });
        setTimeout(() => {
          setVoiceListening(false);
          setQuery("Deploy current project");
          toast({ title: t("cmdk.toast.voiceCaptured"), description: t("cmdk.toast.processing") });
        }, 3000);
      }
      return !prev;
    });
  }, [t]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || (e.key === "j" && e.ctrlKey)) {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp" || (e.key === "k" && e.ctrlKey)) {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flatItems[selectedIndex];
      if (!item) return;
      if (item.type === "ai") executeAIBuild();
      else if (item.type === "macro" && item.macro) executeMacro(item.macro);
      else if (item.cmd) executeCommand(item.cmd);
    } else if (e.key === "Tab") {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % Math.max(flatItems.length, 1));
    }
  }, [flatItems, selectedIndex, executeCommand, executeAIBuild, executeMacro]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selectedIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  let flatIdx = 0;

  const modeColors = {
    default: "border-border/40",
    voice: "border-green-500/50 shadow-green-500/10",
    macro: "border-amber-500/50 shadow-amber-500/10",
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="fixed inset-0 z-50 bg-background/60 backdrop-blur-md"
            onClick={() => setOpen(false)}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -12 }}
            transition={{ type: "spring", damping: 25, stiffness: 350 }}
            className="fixed left-1/2 top-[10%] z-50 w-[95vw] max-w-[640px] -translate-x-1/2"
          >
            <div className={cn(
              "overflow-hidden rounded-2xl border bg-card/95 backdrop-blur-xl shadow-2xl shadow-black/20 transition-colors duration-300",
              modeColors[mode]
            )}>
              {/* Mode indicator */}
              {mode !== "default" && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  className="flex items-center gap-2 border-b border-border/20 bg-primary/5 px-4 py-1.5"
                >
                  {mode === "voice" && (
                    <>
                      <Mic className="h-3.5 w-3.5 text-green-500 animate-pulse" />
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-green-500">{t("cmdk.voiceMode")}</span>
                    </>
                  )}
                </motion.div>
              )}

              {/* Input Row */}
              <div className="flex items-center gap-3 border-b border-border/30 px-4">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
                  onKeyDown={handleKeyDown}
                  placeholder={t("cmdk.searchPlaceholder")}
                  className="flex h-12 w-full bg-transparent py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60"
                />
                <div className="flex items-center gap-1.5 shrink-0">
                  {query.trim() && (
                    <span className="text-[10px] text-muted-foreground/50 tabular-nums">{resultCount}</span>
                  )}
                  {/* Voice toggle */}
                  <button
                    onClick={toggleVoice}
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-lg transition-all",
                      voiceListening
                        ? "bg-green-500/20 text-green-500 ring-1 ring-green-500/30"
                        : "text-muted-foreground/50 hover:text-foreground hover:bg-muted/50"
                    )}
                    title={t("cmdk.voiceInput")}
                  >
                    {voiceListening ? <Mic className="h-3.5 w-3.5 animate-pulse" /> : <MicOff className="h-3.5 w-3.5" />}
                  </button>
                  {/* Undo */}
                  {undoStack.length > 0 && (
                    <button
                      onClick={handleUndo}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground/50 hover:text-foreground hover:bg-muted/50 transition-all"
                      title={t("cmdk.undoLabel", { label: undoStack[0]?.label ?? "" })}
                    >
                      <Undo2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => setOpen(false)}
                    className="flex h-6 w-6 items-center justify-center rounded-md border border-border/50 bg-muted/40 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* Confirmation overlay */}
              <AnimatePresence>
                {confirmAction && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="border-b border-destructive/20 bg-destructive/5 px-4 py-3"
                  >
                    <p className="text-sm font-medium text-destructive">{t("cmdk.confirmLabel", { label: confirmAction.label })}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{confirmAction.description}</p>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => runCommand(confirmAction)} className="px-3 py-1 text-xs rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors">
                        {t("cmdk.confirm")}
                      </button>
                      <button onClick={() => setConfirmAction(null)} className="px-3 py-1 text-xs rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors">
                        {t("cmdk.cancel")}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Results */}
              <div ref={listRef} className="max-h-[400px] overflow-y-auto p-1.5 scroll-smooth">
                {/* AI Build Card */}
                {showAIBuild && (() => {
                  const thisIdx = flatIdx++;
                  return (
                    <div className="mb-1.5">
                      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-primary/70 flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3" />
                        {t("cmdk.aiBuild")}
                      </div>
                      <button
                        data-idx={thisIdx}
                        onClick={executeAIBuild}
                        onMouseEnter={() => setSelectedIndex(thisIdx)}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all",
                          selectedIndex === thisIdx ? "bg-primary/10 ring-1 ring-primary/20" : "hover:bg-muted/40"
                        )}
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/60">
                          <Sparkles className="h-4 w-4 text-primary-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{t("cmdk.buildWithAI")}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">"{query}"</p>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      </button>
                    </div>
                  );
                })()}

                {/* Macro match */}
                {matchedMacro && (() => {
                  const thisIdx = flatIdx++;
                  return (
                    <div className="mb-1.5">
                      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-amber-500/70 flex items-center gap-1.5">
                        <Play className="h-3 w-3" />
                        {t("cmdk.macro")}
                      </div>
                      <button
                        data-idx={thisIdx}
                        onClick={() => executeMacro(matchedMacro)}
                        onMouseEnter={() => setSelectedIndex(thisIdx)}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all",
                          selectedIndex === thisIdx ? "bg-amber-500/10 ring-1 ring-amber-500/20" : "hover:bg-muted/40"
                        )}
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-amber-600">
                          <Hash className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{matchedMacro.name}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{t("cmdk.stepsCount", { n: matchedMacro.steps.length })}</p>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      </button>
                    </div>
                  );
                })()}

                {/* Sections */}
                {sections.map((section) => (
                  <div key={section.label} className="mb-1">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                      <section.icon className="h-3 w-3" />
                      {section.label}
                    </div>
                    {section.items.map((cmd) => {
                      const thisIdx = flatIdx++;
                      const Icon = getIcon(cmd.icon);
                      const fav = isFavorite(cmd.id);
                      return (
                        <button
                          key={cmd.id}
                          data-idx={thisIdx}
                          onClick={() => executeCommand(cmd)}
                          onMouseEnter={() => setSelectedIndex(thisIdx)}
                          className={cn(
                            "group flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-all",
                            selectedIndex === thisIdx ? "bg-accent/80 text-accent-foreground" : "text-muted-foreground hover:bg-muted/40",
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 truncate text-sm">{cmd.label}</span>
                          {cmd.description && (
                            <span className="hidden md:inline text-[10px] text-muted-foreground/50 truncate max-w-[140px]">
                              {cmd.description}
                            </span>
                          )}
                          <button
                            onClick={(e) => handleToggleFavorite(e, cmd.id)}
                            className={cn(
                              "h-5 w-5 shrink-0 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity",
                              fav && "opacity-100"
                            )}
                          >
                            <Star className={cn("h-3 w-3", fav ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40")} />
                          </button>
                          {cmd.shortcut && (
                            <kbd className="hidden sm:inline-flex h-5 items-center rounded border border-border/50 bg-muted/60 px-1.5 font-mono text-[10px] text-muted-foreground/60">
                              {cmd.shortcut}
                            </kbd>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}

                {/* Empty states */}
                {flatItems.length === 0 && query.trim() && (
                  <div className="py-10 text-center">
                    <Search className="mx-auto h-8 w-8 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground/70">{t("cmdk.noMatch", { query })}</p>
                    <p className="text-xs text-muted-foreground/40 mt-1">{t("cmdk.noMatchHint")}</p>
                  </div>
                )}

                {flatItems.length === 0 && !query.trim() && (
                  <div className="py-8 text-center">
                    <Command className="mx-auto h-6 w-6 text-muted-foreground/30 mb-2" />
                    <p className="text-xs text-muted-foreground/50">{t("cmdk.emptyHint")}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-border/30 px-4 py-2">
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground/50 flex-wrap">
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-border/50 bg-muted/50 px-1 font-mono">↑↓</kbd> {t("cmdk.hint.nav")}
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-border/50 bg-muted/50 px-1 font-mono">↵</kbd> {t("cmdk.hint.run")}
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-border/50 bg-muted/50 px-1 font-mono">⌘Z</kbd> {t("cmdk.hint.undo")}
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-border/50 bg-muted/50 px-1 font-mono">/</kbd> {t("cmdk.hint.macro")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {mode === "voice" && (
                    <span className="text-[10px] text-green-500 font-medium">{t("cmdk.voiceBadge")}</span>
                  )}
                  <span className="text-[10px] text-muted-foreground/40">⌘K · Nuvra</span>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
